// src/components/forms/shared/PetSelectionRow.tsx

import React from 'react';
import { Box, Typography, styled } from '@mui/material';
import {
  ContentCut as GroomingIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { getCustomIcon } from '../../../utils/customIcons';
import { StyledSwitch } from './FormElements';
import { StyledIconButton } from '../../common/Buttons';

interface PetSelectionRowProps {
  petId: number;
  petName: string;
  petType: string;
  petBreed?: string;
  isSelected: boolean;
  onToggle: (petId: number) => void;
  disabled?: boolean;
  numberOfNights?: number;
  bookingLength?: number;
  useSharingRate?: boolean;
  useCustomRate?: boolean;
  groomingCount?: number;
  stayLength?: number;
  priceSingle?: number;
  priceSharing?: number;
  priceGroomingSmooth?: number;
  priceGroomingLong?: number;
  priceCut?: number;
  discountPercentage?: number;
  // Shown after the breed, e.g. "no longer owned"
  statusNote?: string;
  // Disables grooming and stay edits, leaving only the switch
  lockDetails?: boolean;
  onGroomingClick?: (
    petId: number,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void;
  onEditClick?: (
    petId: number,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void;
}

// Styled container for the entire row - dynamically styled based on selection
const RowContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  fontStyle: isSelected ? 'normal' : 'italic',
  minHeight: '4rem',
  padding: '0 0',
  borderBottom: `3px dashed ${theme.palette.dividerContrast}`,
  opacity: isSelected ? 1 : 0.4,
  transition: 'opacity 0.2s ease',

  '&:last-child': {
    borderBottom: 'none',
  },
  // Desktop: WIDER ROW
  [theme.breakpoints.up('md')]: {
    maxWidth: '800px',
  },
}));

// Desktop Left side: Icon + Name + Breed with overflow handling
const LeftContentDesktop = styled(Box)(({ theme }) => ({
  display: 'none',

  // Desktop: show this version with ellipses on overflow
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    flex: 1,
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollBehavior: 'smooth',
    borderRight: `4px solid ${theme.palette.dividerContrast}`,
    paddingLeft: '0.75rem',
    boxShadow: `inset -2.3rem 0 1.5rem -1rem ${
      theme.palette.mode === 'dark'
        ? 'rgba(0, 0, 0, 0.4)'
        : 'rgba(0, 0, 0, 0.195)'
    }`,
    // Hide scrollbar while keeping scroll functionality
    scrollbarWidth: 'none',
    height: 'stretch',
  },
}));

// Mobile Left side: Icon + Name + Breed - scrollable
const LeftContentMobile = styled(Box)(({ theme }) => ({
  display: 'none',

  // Mobile: show this version
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    flex: 1,
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    paddingRight: '0.5rem',
    WebkitOverflowScrolling: 'touch',
    scrollBehavior: 'smooth',
    borderRight: `4px solid ${theme.palette.dividerContrast}`,
    boxShadow: `inset -2.5rem 0 1.5rem -1rem ${
      theme.palette.mode === 'dark'
        ? 'rgba(0, 0, 0, 0.4)'
        : 'rgba(0, 0, 0, 0.15)'
    }`,

    // Hide scrollbar while keeping scroll functionality
    scrollbarWidth: 'none',

    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
}));

// Pet type icon container
const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'useCustomRate',
})<{ useCustomRate?: boolean }>(({ theme, useCustomRate }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '3rem',
  height: '3rem',
  flexShrink: 0,
  position: 'relative',

  '& svg': {
    fontSize: '2.5rem',
    color: theme.palette.text.primary,
  },

  // Conditional ::after for custom rate border
  ...(useCustomRate && {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '3rem',
      height: '3rem',
      borderRadius: '50%',
      border: `5px solid ${
        theme.palette.mode === 'dark'
          ? theme.palette.secondary.main
          : theme.palette.secondary.main
      }`,
      pointerEvents: 'none',
    },
  }),
}));

// Pet name text -
const PetNameText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  userSelect: 'none',
  whiteSpace: 'nowrap',
  flexShrink: 0,
}));

// PetSelectionRow renders both a desktop and a mobile PetNameText for the
// same pet (only one is visually shown at a time via CSS breakpoints), so
// each needs its own id to avoid a duplicate-id collision - the switch
// below only needs to point at one of them for a real accessible name.

// Pet breed text
const PetBreedText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  userSelect: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,

  // Desktop: allow shrinking for ellipsis
  [theme.breakpoints.up('md')]: {
    flexShrink: 1,
  },

  // Mobile: prevent shrinking to keep content wide for scrolling
  [theme.breakpoints.down('md')]: {
    flexShrink: 0,
  },
}));

// Right side: Action Icons + Switch
const RightContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.2rem',
  flexShrink: 0,
  marginLeft: 'auto',
  // Desktop: allow shrinking for ellipsis
  [theme.breakpoints.up('md')]: {
    flex: 0.4,
    justifyContent: 'space-around',
  },
}));

// Grooming Placeholder - reserves space when grooming unavailable
const GroomingPlaceholder = styled(Box)({
  width: '3rem',
  height: '3rem',
  flexShrink: 0,
});

// Number Overlay for Icon Badges
const NumberOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '-3px',
  right: 0,
  left: 0,
  fontWeight: 900,
  fontSize: '2.2rem',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main,
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '1.5rem',
  minHeight: '1.5rem',
}));

// Number Overlay Small for dual overlays (superscript/subscript)
const NumberOverlaySmall = styled(Box)(({ theme }) => ({
  position: 'absolute',
  fontWeight: 900,
  fontSize: '1.4rem',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main,
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '1.2rem',
  minHeight: '1.2rem',
}));

const PetStatusText = styled(PetBreedText)({
  fontWeight: 700,
  flexShrink: 0,
  marginLeft: '0.5rem',
});

const NumberOverlaySmallBlack = styled(Box)(({ theme }) => ({
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.text.primary
      : theme.palette.text.primary,
}));

const PetSelectionRow: React.FC<PetSelectionRowProps> = ({
  petId,
  petName,
  petType,
  petBreed,
  isSelected,
  onToggle,
  disabled = false,
  useSharingRate = false,
  useCustomRate = false,
  groomingCount = 0,
  stayLength,
  bookingLength,
  priceSingle,
  priceSharing,
  priceGroomingSmooth,
  priceGroomingLong,
  priceCut,
  discountPercentage = 0,
  statusNote,
  lockDetails = false,
  onGroomingClick,
  onEditClick,
}) => {
  // Determine if stay length differs from booking length
  const stayLengthDifference =
    stayLength !== undefined &&
    bookingLength !== undefined &&
    stayLength !== bookingLength
      ? stayLength
      : null;

  // Check if grooming is available for this pet (any grooming price > 0)
  const hasGroomingAvailable = (): boolean => {
    return (
      (priceGroomingSmooth !== undefined && priceGroomingSmooth > 0) ||
      (priceGroomingLong !== undefined && priceGroomingLong > 0) ||
      (priceCut !== undefined && priceCut > 0)
    );
  };

  // Conditional positioning for stay length overlay
  // Center vertically if no discount exists, otherwise position at top
  const stayLengthSx = {
    ...(discountPercentage > 0
      ? { top: '-3px' }
      : { top: '50%', transform: 'translateY(-50%)' }),
    left: '-12px',
    '@media (max-width: 600px)': {
      left: 0,
      right: 0,
    },
  };

  // Conditional positioning for discount overlay
  // Center vertically if no stay length exists, otherwise position at bottom
  const discountSx = {
    ...(stayLengthDifference !== null
      ? { bottom: '-8px' }
      : { bottom: '50%', transform: 'translateY(50%)' }),
    left: '-6px',
    '@media (max-width: 600px)': {
      left: 0,
      right: 0,
    },
  };

  return (
    <RowContainer isSelected={isSelected}>
      {/* DESKTOP: with ellipses and tooltip on overflow */}
      <LeftContentDesktop>
        {/* Pet Type Icon */}
        <IconContainer useCustomRate={useCustomRate}>
          {/* Primary icon with inverted opacity when sharing */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: useSharingRate ? 0.39 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            {getCustomIcon(petType, '2.5rem')}
          </Box>

          {/* Overlay icon - positioned top-right */}
          {useSharingRate && (
            <Box
              sx={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                opacity: 1,
                transform: 'scale(0.75)',
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getCustomIcon(petType, '2.5rem')}
            </Box>
          )}
        </IconContainer>

        {/* Pet Name */}
        <PetNameText id={`pet-selection-${petId}-name-desktop`}>
          {petName}
        </PetNameText>

        <PetBreedText> - {petBreed || petType}</PetBreedText>
        {statusNote && (
          <PetStatusText id={`pet-selection-${petId}-status-desktop`}>
            ({statusNote})
          </PetStatusText>
        )}
      </LeftContentDesktop>

      {/* MOBILE: scrollable */}
      <LeftContentMobile>
        {/* Pet Type Icon */}
        <IconContainer useCustomRate={useCustomRate}>
          {/* Primary icon with inverted opacity when sharing */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: useSharingRate ? 0.65 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            {getCustomIcon(petType, '2.5rem')}
          </Box>

          {/* Overlay icon - positioned top-right */}
          {useSharingRate && (
            <Box
              sx={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                opacity: 1,
                transform: 'scale(0.75)',
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getCustomIcon(petType, '2.5rem')}
            </Box>
          )}
        </IconContainer>

        {/* Pet Name */}
        <PetNameText id={`pet-selection-${petId}-name-mobile`}>
          {petName}
        </PetNameText>

        <PetBreedText> - {petBreed || petType}</PetBreedText>
        {statusNote && (
          <PetStatusText id={`pet-selection-${petId}-status-mobile`}>
            ({statusNote})
          </PetStatusText>
        )}
      </LeftContentMobile>

      {/* Right: Icons + Switch */}
      <RightContent>
        {/* Grooming Icon - Only visible if grooming available */}
        {hasGroomingAvailable() ? (
          <Box sx={{ position: 'relative', width: '3rem', height: '3rem' }}>
            <StyledIconButton
              variant="bold"
              size="small"
              onClick={(event) => onGroomingClick?.(petId, event)}
              disabled={disabled || lockDetails || !isSelected}
              sx={{
                width: '3rem',
                height: '3rem',
                opacity: groomingCount > 0 ? 0.3 : 1,
                transition: 'opacity 0.2s ease',
                '& svg': {
                  fontSize: '2rem',
                },
              }}
            >
              <GroomingIcon />
            </StyledIconButton>
            {groomingCount > 0 && (
              <NumberOverlay>{groomingCount}</NumberOverlay>
            )}
          </Box>
        ) : (
          <GroomingPlaceholder />
        )}

        {/* Edit Icon with Dual Number Overlays (superscript/subscript) */}
        <Box sx={{ position: 'relative', width: '3rem', height: '3rem' }}>
          <StyledIconButton
            variant="bold"
            size="small"
            onClick={(event) => onEditClick?.(petId, event)}
            disabled={disabled || lockDetails || !isSelected}
            sx={{
              width: '3rem',
              height: '3rem',
              opacity:
                stayLengthDifference !== null || discountPercentage > 0
                  ? 0.3
                  : 1,
              transition: 'opacity 0.2s ease',
              '& svg': {
                fontSize: '2rem',
              },
            }}
          >
            <EditIcon />
          </StyledIconButton>

          {/* Stay Length Overlay - Top Right (Superscript) */}
          {stayLengthDifference !== null && (
            <NumberOverlaySmall sx={stayLengthSx}>
              {stayLengthDifference}
              <NumberOverlaySmallBlack>
                /{bookingLength}
              </NumberOverlaySmallBlack>
            </NumberOverlaySmall>
          )}

          {/* Discount Overlay - Bottom Left (Subscript) */}
          {discountPercentage > 0 && (
            <NumberOverlaySmall sx={discountSx}>
              {discountPercentage}
              <NumberOverlaySmallBlack>%</NumberOverlaySmallBlack>
            </NumberOverlaySmall>
          )}
        </Box>

        {/* Switch */}
        <StyledSwitch
          id={`pet-selection-${petId}-toggle`}
          inputProps={{
            'aria-labelledby': statusNote
              ? `pet-selection-${petId}-name-desktop pet-selection-${petId}-status-desktop`
              : `pet-selection-${petId}-name-desktop`,
          }}
          checked={isSelected}
          onChange={() => onToggle(petId)}
          disabled={disabled}
        />
      </RightContent>
    </RowContainer>
  );
};

export default PetSelectionRow;
